/**
 * Durable state of a `build_workflow` handoff, keyed by the tool call's
 * `callId` and kept in `localStorage` so it survives reloads and is shared
 * across tabs. Every marker is mirrored in module scope so reads stay
 * synchronous and keep working when storage is unavailable (private mode,
 * quota). See docs/workflow-ai-chat/README.md ("After-call automations build
 * in the background").
 *
 * - `workflow-build-handoff-{callId}`: the workflow the handoff created.
 *   Written as soon as the draft exists, so a reload mid-build resumes it
 *   instead of creating a duplicate.
 * - `...-published`: the after-call build published; a revisit never rebuilds.
 * - `...-notified`: the continuation message was accepted by the chat; a
 *   reload never re-sends it, but a dropped send is retried.
 * - `...-claim`: which tab is currently driving the build (with a timestamp,
 *   so a tab that died releases the claim after `HANDOFF_CLAIM_TTL_MS`).
 */

const KEY_PREFIX = "workflow-build-handoff-";

export function handoffLinkKey(callId: string): string {
   return `${KEY_PREFIX}${callId}`;
}

export function handoffPublishedKey(callId: string): string {
   return `${KEY_PREFIX}${callId}-published`;
}

export function handoffNotifiedKey(callId: string): string {
   return `${KEY_PREFIX}${callId}-notified`;
}

export function handoffClaimKey(callId: string): string {
   return `${KEY_PREFIX}${callId}-claim`;
}

/** True for any marker of this handoff; used to filter cross-tab `storage` events. */
export function isHandoffKey(key: string, callId: string): boolean {
   return key === handoffLinkKey(callId) || key.startsWith(`${handoffLinkKey(callId)}-`);
}

function readItem(key: string): string | null {
   try {
      if (typeof window === "undefined") return null;
      return window.localStorage.getItem(key);
   } catch {
      return null;
   }
}

function writeItem(key: string, value: string): void {
   try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(key, value);
   } catch {
      // Storage may be unavailable; the in-memory mirrors still hold.
   }
}

function removeItem(key: string): void {
   try {
      if (typeof window === "undefined") return;
      window.localStorage.removeItem(key);
   } catch {
      // Nothing to clean up when storage is unavailable.
   }
}

const linkedWorkflowIds = new Map<string, string>();
const publishedHandoffs = new Set<string>();
const notifiedHandoffs = new Set<string>();

export function readLinkedWorkflowId(callId: string): string | null {
   const stored = readItem(handoffLinkKey(callId));
   if (stored) {
      linkedWorkflowIds.set(callId, stored);
      return stored;
   }
   return linkedWorkflowIds.get(callId) ?? null;
}

export function writeLinkedWorkflowId(callId: string, workflowId: string): void {
   linkedWorkflowIds.set(callId, workflowId);
   writeItem(handoffLinkKey(callId), workflowId);
}

function readFlag(key: string, mirror: Set<string>, callId: string): boolean {
   if (readItem(key) === "1") {
      mirror.add(callId);
      return true;
   }
   return mirror.has(callId);
}

export function readHandoffPublished(callId: string): boolean {
   return readFlag(handoffPublishedKey(callId), publishedHandoffs, callId);
}

export function writeHandoffPublished(callId: string): void {
   publishedHandoffs.add(callId);
   writeItem(handoffPublishedKey(callId), "1");
}

export function readHandoffNotified(callId: string): boolean {
   return readFlag(handoffNotifiedKey(callId), notifiedHandoffs, callId);
}

export function writeHandoffNotified(callId: string): void {
   notifiedHandoffs.add(callId);
   writeItem(handoffNotifiedKey(callId), "1");
}

// ---------------------------------------------------------------------------
// Cross-tab claim
// ---------------------------------------------------------------------------

/** Longer than a whole build (the builder stream alone may run two minutes). */
export const HANDOFF_CLAIM_TTL_MS = 5 * 60 * 1000;

/**
 * How long to wait after writing a claim before trusting it. Two tabs that
 * read "no claim" at the same instant both write; the re-read after this
 * pause sees whichever write landed last, so at most one tab proceeds.
 */
export const HANDOFF_CLAIM_SETTLE_MS = 30;

interface HandoffClaim {
   owner: string;
   ts: number;
}

/**
 * Identifies this JS context (one per tab, reset on reload). Only needs to
 * be unique across the tabs of one browser profile, so Web Crypto covers it
 * (`randomUUID` is limited to secure contexts; `getRandomValues` is not).
 */
function newContextId(): string {
   if (typeof crypto === "undefined") return `ctx-${Date.now().toString(36)}`;
   if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
   const bytes = crypto.getRandomValues(new Uint8Array(16));
   return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

const CONTEXT_ID = newContextId();

/** callIds this context is driving right now; a synchronous guard against duplicate starts. */
const inFlightHandoffs = new Set<string>();

function parseClaim(raw: string | null): HandoffClaim | null {
   if (!raw) return null;
   try {
      const parsed = JSON.parse(raw) as Partial<HandoffClaim>;
      if (typeof parsed.owner !== "string" || typeof parsed.ts !== "number") return null;
      return {owner: parsed.owner, ts: parsed.ts};
   } catch {
      return null;
   }
}

function isLiveForeignClaim(claim: HandoffClaim | null, now: number): boolean {
   return claim !== null && claim.owner !== CONTEXT_ID && now - claim.ts < HANDOFF_CLAIM_TTL_MS;
}

/** True when another tab holds a fresh claim on this handoff. */
export function isHandoffClaimedElsewhere(callId: string, now = Date.now()): boolean {
   return isLiveForeignClaim(parseClaim(readItem(handoffClaimKey(callId))), now);
}

const settle = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Claims the handoff for this context. Resolves `false` when it is already
 * in flight here or claimed by another live tab, so only one tab creates the
 * draft. Callers must `releaseHandoff` when done.
 */
export async function claimHandoff(
   callId: string,
   options: {now?: () => number; settleMs?: number} = {}
): Promise<boolean> {
   if (inFlightHandoffs.has(callId)) return false;
   inFlightHandoffs.add(callId);

   const now = options.now ?? Date.now;
   const key = handoffClaimKey(callId);
   if (isLiveForeignClaim(parseClaim(readItem(key)), now())) {
      inFlightHandoffs.delete(callId);
      return false;
   }
   writeItem(key, JSON.stringify({owner: CONTEXT_ID, ts: now()} satisfies HandoffClaim));
   await settle(options.settleMs ?? HANDOFF_CLAIM_SETTLE_MS);
   const confirmed = parseClaim(readItem(key));
   if (confirmed && confirmed.owner !== CONTEXT_ID) {
      inFlightHandoffs.delete(callId);
      return false;
   }
   return true;
}

/** Releases this context's claim; a claim another tab took over is left alone. */
export function releaseHandoff(callId: string): void {
   inFlightHandoffs.delete(callId);
   const key = handoffClaimKey(callId);
   const claim = parseClaim(readItem(key));
   if (!claim || claim.owner === CONTEXT_ID) removeItem(key);
}
